"use client";

import { useTranslations } from "next-intl";
import { getCountryCode, getCountryName } from "@forestsync/db";
import { ProjectPhotoCarousel } from "./ProjectPhotoCarousel";
import {
  Paper,
  Text,
  Badge,
  Group,
  Stack,
  Grid,
  DetailRow,
  Avatar,
  TbMapPin,
  TbRuler,
  TbCalendar,
  TbShield,
  TbLeaf,
  FaLeaf,
  TbCertificate,
  TbTarget,
  TbHomeEco,
  TbAward,
  TbExternalLink,
  Anchor,
} from "@forestsync/ui";
import { type CarbonSinkDetail } from "@/types/glassroom";
import { SinkMap } from "./SinkMap";
import { HyperLink } from "../common/HyperLink";

interface SinkDetailViewProps {
  sink: CarbonSinkDetail;
}

export function SinkDetailView({ sink }: SinkDetailViewProps) {
  const t = useTranslations();

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const stats = [
    {
      label: t("sink.carbonStorage"),
      value: `${(sink.co2Storage ?? 0).toLocaleString()} ${t("common.tons")} ${t("common.co2")}`,
      icon: <FaLeaf size={20} className="text-green-600" />,
      color: "green",
    },
    {
      label: `${t("sink.tokens")} ${t("sink.available")}`,
      value: `${sink.availableTokens?.toLocaleString() || 0}`,
      icon: <TbLeaf size={20} className="text-blue-600" />,
      color: "blue",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <TbLeaf size={30} className="text-forest-600 dark:text-forest-300" />
          <div>
            <Text size="xl" fw={700} className="text-gray-900 dark:text-white">
              {sink.name}
            </Text>
            <Text
              size="sm"
              className="text-gray-500 dark:text-gray-400 font-mono"
            >
              {sink.refId}
            </Text>
          </div>
        </div>

        {/* Inline Stats */}
        <div className="flex items-center gap-6">
          {sink.ownerLogoUrl && (
            <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 shrink-0 leading-none px-2 py-1">
              <img
                src={sink.ownerLogoUrl}
                alt={sink.ownerName}
                className="h-9 w-auto max-w-[100px] object-contain block"
              />
            </div>
          )}
          {stats.map((stat, index) => (
            <div key={index} className="flex items-center gap-2">
              {stat.icon}
              <div>
                <Text
                  size="sm"
                  fw={600}
                  className="text-gray-700 dark:text-gray-300"
                >
                  {stat.label}
                </Text>
                <Text
                  size="sm"
                  fw={600}
                  className="text-gray-900 dark:text-white"
                >
                  {stat.value}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overview and Map Row */}
      <Grid align="stretch">
        {/* Project Overview */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Paper withBorder p="lg" radius="md" h="100%">
            <Stack gap="md">
              <Text className="text-gray-700 dark:text-gray-300">
                {sink.description}
              </Text>

              <Grid>
                {/* Location */}
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <DetailRow
                    icon={TbMapPin}
                    label={t("common.location")}
                    value={[sink.municipality, sink.region, getCountryName(getCountryCode(sink.country))].filter(Boolean).join(", ")}
                    iconSize={20}
                    layout="vertical"
                  />
                </Grid.Col>
                {/* Size — hidden for credit types that don't have area */}
                {sink.sizeHa != null && (
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <DetailRow
                      icon={TbRuler}
                      label={t("common.size")}
                      value={`${(sink.sizeHa ?? 0).toLocaleString()} ${t("common.hectares")}`}
                      iconSize={20}
                      layout="vertical"
                    />
                  </Grid.Col>
                )}
                {/* Planted — hidden for credit types where plantedDate is not applicable */}
                {sink.plantedDate != null && (
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <DetailRow
                      icon={TbCalendar}
                      label={t("sink.planted")}
                      value={`${formatDate(sink.plantedDate)} (${t("common.yearsOld", { age: sink.age })})`}
                      iconSize={20}
                      layout="vertical"
                    />
                  </Grid.Col>
                )}
                {/* Last Verified */}
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <DetailRow
                    icon={TbShield}
                    label={t("sink.lastVerified")}
                    value={
                      sink.lastVerificationDate ? (
                        formatDate(sink.lastVerificationDate)
                      ) : (
                        <Badge variant="light" size="sm">
                          {t("sink.dataSyncPending") || "Data sync pending"}
                        </Badge>
                      )
                    }
                    iconSize={20}
                    layout="vertical"
                  />
                </Grid.Col>
                {/* VVB Status */}
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <DetailRow
                    icon={TbAward}
                    label={t("sink.vvbStatus")}
                    value={
                      <Badge variant="light" size="sm">
                        {t("sink.dataSyncPending") || "Data sync pending"}
                      </Badge>
                    }
                    iconSize={20}
                    layout="vertical"
                  />
                </Grid.Col>
                {/* Project Developer */}
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <DetailRow
                    icon={TbHomeEco}
                    label={t("sink.projectDeveloper")}
                    value={
                      <HyperLink type="pd" refId={sink.ownerRefId}>
                        {sink.ownerName}
                      </HyperLink>
                    }
                    iconSize={20}
                    layout="vertical"
                  />
                </Grid.Col>
                {/* Credit Lifespan — hidden when not applicable */}
                {sink.lifespan != null && (
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <DetailRow
                      icon={TbTarget}
                      label={t("sink.lifespan")}
                      value={`${sink.lifespan}+ ${t("common.years")}`}
                      iconSize={20}
                      layout="vertical"
                    />
                  </Grid.Col>
                )}
              </Grid>

              {/* Project Details, SDG Goals and Certifications at bottom of overview */}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <Grid>
                  {/* First Row: Project Type and Methodology */}
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text
                      size="sm"
                      fw={600}
                      mb="sm"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t("sink.projectType")}
                    </Text>
                    <Text
                      size="sm"
                      className="text-gray-600 dark:text-gray-400"
                    >
                      {sink.projectType === 1
                        ? t("sink.exAnte")
                        : sink.projectType === 2
                          ? t("sink.exPost")
                          : "N/A"}
                    </Text>
                  </Grid.Col>

                  {sink.methodology && (
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Text
                        size="sm"
                        fw={600}
                        mb="sm"
                        className="text-gray-700 dark:text-gray-300"
                      >
                        {t("sink.methodology")}
                      </Text>
                      <Text
                        size="sm"
                        className="text-gray-600 dark:text-gray-400"
                      >
                        {sink.methodology}
                      </Text>
                    </Grid.Col>
                  )}

                  {/* Second Row: SDG Goals and Certifications */}
                  {sink.sdgGoals && sink.sdgGoals.length > 0 && (
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Text
                        size="sm"
                        fw={600}
                        mb="sm"
                        className="text-gray-700 dark:text-gray-300"
                      >
                        {t("sdg.title")}
                      </Text>
                      <Group gap="sm">
                        {sink.sdgGoals
                          .sort((a, b) => a - b)
                          .map((goal) => (
                            <Avatar
                              key={goal}
                              src={`/SDGs/sdg-${goal}.png`}
                              alt={`SDG ${goal}: ${t(`sdg.${goal}`)}`}
                              size="sm"
                              radius="sm"
                              className="hover:scale-105 transition-transform duration-200"
                              title={`SDG ${goal}: ${t(`sdg.${goal}`)}`}
                            />
                          ))}
                      </Group>
                    </Grid.Col>
                  )}

                  {sink.certifications && sink.certifications.length > 0 && (
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Text
                        size="sm"
                        fw={600}
                        mb="sm"
                        className="text-gray-700 dark:text-gray-300"
                      >
                        {t("sink.certifications")}
                      </Text>
                      <Group gap="xs">
                        {sink.certifications.map((cert, index) => (
                          <Badge
                            key={index}
                            variant="light"
                            color="green"
                            size="sm"
                            leftSection={<TbCertificate size={10} />}
                          >
                            {cert}
                          </Badge>
                        ))}
                      </Group>
                    </Grid.Col>
                  )}

                  {sink.externalRegistries && sink.externalRegistries.length > 0 && (
                    <Grid.Col span={12}>
                      <Text
                        size="sm"
                        fw={600}
                        mb="sm"
                        className="text-gray-700 dark:text-gray-300"
                      >
                        {t("sink.externalRegistries")}
                      </Text>
                      <Group gap="xs">
                        {sink.externalRegistries.map((ref, index) => (
                          ref.url ? (
                            <Anchor
                              key={index}
                              href={ref.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="never"
                            >
                              <Badge
                                variant="light"
                                color="blue"
                                size="sm"
                                leftSection={<TbExternalLink size={10} />}
                                className="cursor-pointer"
                              >
                                {t(`sink.registryNames.${ref.registry}`)} · {ref.projectId}
                              </Badge>
                            </Anchor>
                          ) : (
                            <Badge
                              key={index}
                              variant="light"
                              color="blue"
                              size="sm"
                            >
                              {t(`sink.registryNames.${ref.registry}`)} · {ref.projectId}
                            </Badge>
                          )
                        ))}
                      </Group>
                    </Grid.Col>
                  )}
                </Grid>
              </div>
            </Stack>
          </Paper>
        </Grid.Col>

        {/* Map */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <div className="h-full w-full overflow-hidden min-h-[300px]">
            <SinkMap sink={sink} />
          </div>
        </Grid.Col>
      </Grid>

      {/* Technical Specifications — only rendered when credit-type-specific data exists */}
      {(sink.productionCapacityTonnesPerYear != null ||
        sink.feedstockType ||
        sink.forestAgeYears != null ||
        sink.ecosystemType ||
        sink.deforestationBaselinePercent != null) && (
        <Paper withBorder p="lg" radius="md">
          <Text
            size="sm"
            fw={600}
            mb="md"
            className="text-gray-700 dark:text-gray-300"
          >
            {t("sink.technicalSpecs")}
          </Text>
          <Grid>
            {sink.ecosystemType && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <DetailRow
                  icon={TbLeaf}
                  label={t("sink.ecosystemType")}
                  value={sink.ecosystemType}
                  iconSize={18}
                  layout="vertical"
                />
              </Grid.Col>
            )}
            {sink.feedstockType && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <DetailRow
                  icon={TbLeaf}
                  label={t("sink.feedstockType")}
                  value={sink.feedstockType}
                  iconSize={18}
                  layout="vertical"
                />
              </Grid.Col>
            )}
            {sink.forestAgeYears != null && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <DetailRow
                  icon={TbCalendar}
                  label={t("sink.forestAge")}
                  value={`${sink.forestAgeYears} ${t("common.years")}`}
                  iconSize={18}
                  layout="vertical"
                />
              </Grid.Col>
            )}
            {sink.productionCapacityTonnesPerYear != null && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <DetailRow
                  icon={TbRuler}
                  label={t("sink.productionCapacity")}
                  value={`${sink.productionCapacityTonnesPerYear.toLocaleString()} t/yr`}
                  iconSize={18}
                  layout="vertical"
                />
              </Grid.Col>
            )}
            {sink.deforestationBaselinePercent != null && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <DetailRow
                  icon={TbTarget}
                  label={t("sink.deforestationBaseline")}
                  value={`${sink.deforestationBaselinePercent}%`}
                  iconSize={18}
                  layout="vertical"
                />
              </Grid.Col>
            )}
          </Grid>
        </Paper>
      )}

      {/* Project Photos */}
      {sink.galleryUrls && sink.galleryUrls.length > 0 && (
        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="md" className="text-gray-700 dark:text-gray-300">
            Project Photos
          </Text>
          <ProjectPhotoCarousel urls={sink.galleryUrls} name={sink.name} />
        </Paper>
      )}
    </div>
  );
}
